import { Injectable } from '@angular/core';
import { TranslateService } from '@tolgee/ngx';
import { LocalStorageService } from 'ngx-webstorage';
import { CookieService } from './cookie.service';
import { preferred_lang } from '../signals/translation.signal';

export interface UiLanguage {
    /** Tolgee / backend language code. */
    code: string;
    /** What the switcher shows. */
    label: string;
}

const STORAGE_KEY = 'preferred_lang';

/**
 * Single place that owns the active UI language.
 *
 * Switching does three things: tells Tolgee to load the other JSON, persists the
 * choice (localStorage + cookie) so a reload keeps it, and updates the
 * `preferred_lang` signal — the interceptor sends that as `lang` on every POST,
 * so backend-localised content (projects, space types, automations) follows the
 * UI without extra work.
 */
@Injectable({ providedIn: 'root' })
export class LanguageService {
    readonly languages: UiLanguage[] = [
        { code: 'en', label: 'EN' },
        { code: 'el', label: 'GR' },
    ];

    constructor(
        private tolgee: TranslateService,
        private localStorage: LocalStorageService,
        private cookieService: CookieService,
    ) { }

    /** The active language code, falling back to English. */
    get current(): string {
        const stored = this.localStorage.retrieve(STORAGE_KEY);
        return this.languages.some((l) => l.code === stored) ? stored : 'en';
    }

    async use(code: string): Promise<void> {
        if (!this.languages.some((l) => l.code === code) || code === this.current) {
            return;
        }
        await this.tolgee.changeLanguage(code);
        this.localStorage.store(STORAGE_KEY, code);
        this.cookieService.setCookie(STORAGE_KEY, code, 365);
        preferred_lang.set(code);
    }
}
